import { useCallback, useEffect, useState } from 'react';
import { formatVideoTime } from './formatVideoTime';

const EMPTY_PROGRESS = { currentTime: 0, maxWatched: 0, duration: 0 };

function loadProgress(storageKey, readProgress) {
  if (!storageKey || typeof readProgress !== 'function') {
    return EMPTY_PROGRESS;
  }

  const saved = readProgress(storageKey);
  if (!saved) {
    return EMPTY_PROGRESS;
  }

  return {
    currentTime: Math.max(0, Number(saved.currentTime) || 0),
    maxWatched: Math.max(0, Number(saved.maxWatched) || 0),
    duration: Math.max(0, Number(saved.duration) || 0),
  };
}

/**
 * Playback state for {@link VideoPlayer} — supports no-skip-ahead when `preventSkipAhead` is true.
 * Pass `resetKey` to reset when the active video changes (e.g. guidance step id).
 * Pass `storageKey` with `readProgress` / `writeProgress` to resume from browser storage.
 */
export function useVideoPlayer({
  resetKey,
  preventSkipAhead = false,
  storageKey = null,
  readProgress,
  writeProgress,
} = {}) {
  const identity = storageKey ?? resetKey;
  const [progress, setProgress] = useState(() => loadProgress(storageKey, readProgress));
  const [playing, setPlaying] = useState(false);
  const [loadedKey, setLoadedKey] = useState(identity);

  let resolved = progress;
  let resolvedPlaying = playing;
  if (identity !== loadedKey) {
    resolved = loadProgress(storageKey, readProgress);
    resolvedPlaying = false;
    setLoadedKey(identity);
    setProgress(resolved);
    setPlaying(false);
  }

  const { currentTime, maxWatched, duration } = resolved;
  const complete = duration > 0 && maxWatched >= duration - 0.5;

  useEffect(() => {
    if (!storageKey || loadedKey !== identity) {
      return;
    }
    if (currentTime <= 0 && maxWatched <= 0) {
      return;
    }
    writeProgress?.(storageKey, { currentTime, maxWatched, duration });
  }, [currentTime, duration, identity, loadedKey, maxWatched, storageKey, writeProgress]);

  const syncDuration = useCallback((nextDuration) => {
    if (Number.isFinite(nextDuration) && nextDuration > 0) {
      setProgress((prev) => ({ ...prev, duration: nextDuration }));
    }
  }, []);

  const syncProgress = useCallback((time) => {
    const safeTime = Math.max(0, Number(time) || 0);
    setProgress((prev) => ({
      currentTime: safeTime,
      maxWatched: Math.max(prev.maxWatched, safeTime),
      duration: prev.duration,
    }));
  }, []);

  const togglePlayback = useCallback(() => {
    if (complete) {
      return;
    }
    setPlaying((value) => !value);
  }, [complete]);

  const seek = useCallback((nextTime) => {
    if (duration <= 0) {
      return null;
    }
    const clamped = Math.max(0, Math.min(duration, nextTime));
    if (!preventSkipAhead || clamped <= maxWatched + 0.25) {
      setProgress((prev) => ({ ...prev, currentTime: clamped }));
      return clamped;
    }
    return null;
  }, [duration, maxWatched, preventSkipAhead]);

  const handleEnded = useCallback(() => {
    setPlaying(false);
    setProgress((prev) => (
      prev.duration > 0
        ? { currentTime: prev.duration, maxWatched: prev.duration, duration: prev.duration }
        : prev
    ));
  }, []);

  return {
    currentTime,
    maxWatched,
    playing: resolvedPlaying,
    setPlaying,
    duration,
    complete,
    syncDuration,
    syncProgress,
    togglePlayback,
    seek,
    handleEnded,
    formatTime: formatVideoTime,
  };
}

export default useVideoPlayer;
